import {Color} from '@benchling/nucleus-ui';
import _ from 'underscore';

import {StandardAminoAcidSLA} from 'biojs/Codons';
import {BasicAminoAcidSLA} from 'molbio/aa-sequences/util/AaSequenceConstants';

export enum AaSequenceColorScheme {
  RASMOL = 'RasMol',
  POLARITY = 'Polarity',
  HYDROPHOBICITY = 'Hydrophobicity',
  NONE = 'None',
}

export type StandardColorMappings =
  | AaSequenceColorScheme.RASMOL
  | AaSequenceColorScheme.POLARITY
  | AaSequenceColorScheme.HYDROPHOBICITY;

export type ColoredAminoAcidSLA = StandardAminoAcidSLA | '?';

export const HYDROPHOBICITY_VALUES: {[aminoAcid in BasicAminoAcidSLA]: number} = {
  // Source: http://assets.geneious.com/manual/8.0/GeneiousManualsu41.html
  F: 1.0,
  L: 0.943,
  I: 0.943,
  Y: 0.88,
  W: 0.878,
  V: 0.825,
  M: 0.738,
  P: 0.711,
  C: 0.68,
  A: 0.616,
  G: 0.501,
  T: 0.45,
  S: 0.359,
  K: 0.283,
  Q: 0.251,
  N: 0.236,
  H: 0.165,
  E: 0.043,
  D: 0.028,
  R: 0.0,
};

export interface AminoAcidColoring {
  backgroundColor: string;
  glyphColor: string;
}

// Use uppercase letters. Any letters not included here will be rendered without any style (black).
export const AMINO_ACID_COLORINGS: {
  [colorScheme in StandardColorMappings]: {
    [aminoAcid in BasicAminoAcidSLA]: AminoAcidColoring;
  };
} = {
  // Source: http://life.nthu.edu.tw/~fmhsu/rasframe/SHAPELY.HTM
  // A and G have been altered to be darker
  [AaSequenceColorScheme.RASMOL]: {
    A: {backgroundColor: '#808080', glyphColor: Color.WHITE}, // Ala
    C: {backgroundColor: '#E6E600', glyphColor: Color.BLACK}, // Cys
    D: {backgroundColor: '#E60A0A', glyphColor: Color.WHITE}, // Asp
    E: {backgroundColor: '#E60A0A', glyphColor: Color.WHITE}, // Glu
    F: {backgroundColor: '#3232AA', glyphColor: Color.WHITE}, // Phe
    G: {backgroundColor: '#C0C0C0', glyphColor: Color.BLACK}, // Gly
    H: {backgroundColor: '#8282D2', glyphColor: Color.BLACK}, // His
    I: {backgroundColor: '#0F820F', glyphColor: Color.WHITE}, // Ile
    K: {backgroundColor: '#145AFF', glyphColor: Color.WHITE}, // Lys
    L: {backgroundColor: '#0F820F', glyphColor: Color.WHITE}, // Leu
    M: {backgroundColor: '#E6E600', glyphColor: Color.BLACK}, // Met
    N: {backgroundColor: '#00DCDC', glyphColor: Color.BLACK}, // Asn
    P: {backgroundColor: '#DC9682', glyphColor: Color.BLACK}, // Pro
    Q: {backgroundColor: '#00DCDC', glyphColor: Color.BLACK}, // Gln
    R: {backgroundColor: '#145AFF', glyphColor: Color.WHITE}, // Arg
    S: {backgroundColor: '#FA9600', glyphColor: Color.BLACK}, // Ser
    T: {backgroundColor: '#FA9600', glyphColor: Color.BLACK}, // Thr
    V: {backgroundColor: '#0F820F', glyphColor: Color.WHITE}, // Val
    W: {backgroundColor: '#B45AB4', glyphColor: Color.BLACK}, // Trp
    Y: {backgroundColor: '#3232AA', glyphColor: Color.WHITE}, // Tyr
  },

  // Source: http://assets.geneious.com/manual/8.0/GeneiousManualsu41.html
  [AaSequenceColorScheme.POLARITY]: {
    A: {backgroundColor: '#E6E600', glyphColor: Color.BLACK},
    C: {backgroundColor: '#00E600', glyphColor: Color.BLACK},
    D: {backgroundColor: '#E60000', glyphColor: Color.WHITE},
    E: {backgroundColor: '#E60000', glyphColor: Color.WHITE},
    F: {backgroundColor: '#E6E600', glyphColor: Color.BLACK},
    G: {backgroundColor: '#E6E600', glyphColor: Color.BLACK},
    H: {backgroundColor: '#0000E6', glyphColor: Color.WHITE},
    I: {backgroundColor: '#E6E600', glyphColor: Color.BLACK},
    K: {backgroundColor: '#0000E6', glyphColor: Color.WHITE},
    L: {backgroundColor: '#E6E600', glyphColor: Color.BLACK},
    M: {backgroundColor: '#E6E600', glyphColor: Color.BLACK},
    N: {backgroundColor: '#00E600', glyphColor: Color.BLACK},
    P: {backgroundColor: '#E6E600', glyphColor: Color.BLACK},
    Q: {backgroundColor: '#00E600', glyphColor: Color.BLACK},
    R: {backgroundColor: '#0000E6', glyphColor: Color.WHITE},
    S: {backgroundColor: '#00E600', glyphColor: Color.BLACK},
    T: {backgroundColor: '#00E600', glyphColor: Color.BLACK},
    V: {backgroundColor: '#E6E600', glyphColor: Color.BLACK},
    W: {backgroundColor: '#E6E600', glyphColor: Color.BLACK},
    Y: {backgroundColor: '#00E600', glyphColor: Color.BLACK},
  },

  [AaSequenceColorScheme.HYDROPHOBICITY]: _.mapObject(HYDROPHOBICITY_VALUES, (val: number) => ({
    // Map hydrophobicity values onto a linear gradient from red to blue
    backgroundColor: `rgb(${Math.round(255 * val)}, 0, ${Math.round(255 * (1 - val))})`,
    glyphColor: Color.WHITE,
  })) as {[aminoAcid in BasicAminoAcidSLA]: AminoAcidColoring},
};

const EXTENDED_GLYPH_COLORINGS = {
  '*': {backgroundColor: '#FFF79A', glyphColor: Color.BLACK},
  '?': {backgroundColor: Color.WHITE, glyphColor: Color.BLACK},
};

export const EXTENDED_AMINO_ACID_COLORINGS: {
  [colorScheme in StandardColorMappings]: {[sla in ColoredAminoAcidSLA]: AminoAcidColoring};
} = {
  [AaSequenceColorScheme.RASMOL]: {
    ...AMINO_ACID_COLORINGS[AaSequenceColorScheme.RASMOL],
    ...EXTENDED_GLYPH_COLORINGS,
  },
  [AaSequenceColorScheme.POLARITY]: {
    ...AMINO_ACID_COLORINGS[AaSequenceColorScheme.POLARITY],
    ...EXTENDED_GLYPH_COLORINGS,
  },
  [AaSequenceColorScheme.HYDROPHOBICITY]: {
    ...AMINO_ACID_COLORINGS[AaSequenceColorScheme.HYDROPHOBICITY],
    ...EXTENDED_GLYPH_COLORINGS,
  },
};

export const TRANSPARENT_SENTINEL = 'TRANSPARENT_SENTINEL';

export function getAminoAcidBackgroundColor(
  sla: ColoredAminoAcidSLA,
  index: number,
  colorScheme: AaSequenceColorScheme
): string {
  if (colorScheme === AaSequenceColorScheme.NONE) {
    if (index % 2 === 1) {
      return Color.GRAY_200;
    } else {
      return Color.GRAY_300;
    }
  } else {
    return EXTENDED_AMINO_ACID_COLORINGS[colorScheme][sla].backgroundColor;
  }
}

export function getAminoAcidGlyphColor(sla: ColoredAminoAcidSLA, colorScheme: AaSequenceColorScheme): string {
  if (colorScheme === AaSequenceColorScheme.NONE) {
    return Color.BLACK;
  }
  return EXTENDED_AMINO_ACID_COLORINGS[colorScheme][sla].glyphColor;
}
